import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Plus, Music, ExternalLink } from 'lucide-react';
import { useSpotify } from '../SpotifyContext';
import { toast } from 'sonner';
import { API_URL } from '../config';

const API = API_URL;

export const SpotifySearchDialog = ({
  isOpen,
  setIsOpen,
  onAddSong
}) => {
  const { isConnected: spotifyConnected, accessToken, addMultipleToQueue } = useSpotify();
  const [searchQuery, setSearchQuery] = useState({ title: '', artist: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedForQueue, setSelectedForQueue] = useState([]);

  const onSearch = async () => {
    if (!searchQuery.title && !searchQuery.artist) {
      toast.error('Ingresa al menos un título o artista');
      return;
    }
    
    if (!spotifyConnected || !accessToken) {
      toast.error('Debes conectar tu cuenta de Spotify primero desde el Dashboard');
      return;
    }
    
    setSearching(true);
    try {
      // Construir query string para Spotify
      const query = [
        searchQuery.title ? `track:${searchQuery.title}` : '',
        searchQuery.artist ? `artist:${searchQuery.artist}` : ''
      ].filter(Boolean).join(' ');
      
      const params = new URLSearchParams({
        query: query,
        access_token: accessToken
      });
      
      const response = await fetch(`${API}/spotify/search?${params}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Error ${response.status}: ${response.statusText}`;
        toast.error(errorMsg);
        setSearchResults([]);
        return;
      }
      
      const data = await response.json();
      
      if (data.tracks && Array.isArray(data.tracks)) {
        setSearchResults(data.tracks);
        if (data.tracks.length === 0) {
          toast.info('No se encontraron resultados');
        } else {
          toast.success(`Se encontraron ${data.tracks.length} resultados`);
        }
      } else {
        toast.error('Respuesta inválida del servidor');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching Spotify:', error);
      toast.error(error.message || 'Error al buscar en Spotify');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xs px-2" size="sm">
          <Music className="w-3.5 h-3.5 mr-1" />
          🎵 Spotify
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle className="text-xl text-white">Buscar en Spotify</DialogTitle>
          <p className="text-slate-400 text-sm mt-2">
            Busca canciones directamente en Spotify y añádelas a tu biblioteca
          </p>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Formulario de búsqueda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="spotify-title">Título</Label>
              <Input
                id="spotify-title"
                className="bg-slate-900 border-slate-700 text-white"
                value={searchQuery.title}
                onChange={(e) => setSearchQuery({ ...searchQuery, title: e.target.value })}
                placeholder="Nombre de la canción"
                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              />
            </div>
            
            <div>
              <Label htmlFor="spotify-artist">Artista</Label>
              <Input
                id="spotify-artist"
                className="bg-slate-900 border-slate-700 text-white"
                value={searchQuery.artist}
                onChange={(e) => setSearchQuery({ ...searchQuery, artist: e.target.value })}
                placeholder="Nombre del artista"
                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              />
            </div>
          </div>

          <Button
            onClick={onSearch}
            disabled={searching || (!searchQuery.title && !searchQuery.artist)}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {searching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Buscando...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Buscar en Spotify
              </>
            )}
          </Button>

          {/* Resultados */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-white font-semibold">
                Resultados ({searchResults.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {searchResults.map((song, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    {/* Checkbox para Queue */}
                    {spotifyConnected && song.spotify_uri && (
                      <input
                        type="checkbox"
                        className="w-5 h-5 sm:w-4 sm:h-4 mt-1 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500 cursor-pointer flex-shrink-0"
                        checked={selectedForQueue.some(s => s.spotify_uri === song.spotify_uri)}
                        onChange={() => {
                          const isSelected = selectedForQueue.some(s => s.spotify_uri === song.spotify_uri);
                          if (isSelected) {
                            setSelectedForQueue(selectedForQueue.filter(s => s.spotify_uri !== song.spotify_uri));
                          } else {
                            setSelectedForQueue([...selectedForQueue, song]);
                          }
                        }}
                        title="Seleccionar para Spotify"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{song.title}</p>
                      <p className="text-slate-400 text-sm truncate">{song.artist}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {song.bpm && (
                          <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded whitespace-nowrap">
                            {song.bpm} BPM
                          </span>
                        )}
                        {song.energia && (
                          <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                            song.energia === 'Muy Alta' ? 'bg-red-900/30 text-red-400' :
                            song.energia === 'Alta' ? 'bg-orange-900/30 text-orange-400' :
                            song.energia === 'Media' ? 'bg-yellow-900/30 text-yellow-400' :
                            'bg-blue-900/30 text-blue-400'
                          }`}>
                            {song.energia === 'Muy Alta' ? '⚡⚡⚡⚡' :
                             song.energia === 'Alta' ? '⚡⚡⚡' :
                             song.energia === 'Media' ? '⚡⚡' : '⚡'} {song.energia}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-2 flex-shrink-0">
                      {song.spotify_url && (
                        <Button
                          onClick={() => window.location.href = song.spotify_url}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 p-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => onAddSong(song)}
                        size="sm"
                        className="bg-cyan-600 hover:bg-cyan-700 p-2 sm:px-3"
                      >
                        <Plus className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Añadir</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {searchResults.length === 0 && !searching && (searchQuery.title || searchQuery.artist) && (
            <div className="text-center py-8">
              <p className="text-slate-400">
                Haz una búsqueda para ver resultados
              </p>
            </div>
          )}

          {/* Botón para enviar seleccionadas a Spotify */}
          {spotifyConnected && selectedForQueue.length > 0 && (
            <div className="pt-4 border-t border-slate-700">
              <Button
                onClick={async () => {
                  try {
                    const uris = selectedForQueue.map(s => s.spotify_uri).filter(Boolean);
                    if (uris.length === 0) {
                      toast.error('Ninguna canción tiene URI de Spotify');
                      return;
                    }

                    await addMultipleToQueue(uris);
                    toast.success(`${uris.length} canciones enviadas a Spotify`);
                    setSelectedForQueue([]);
                  } catch (error) {
                    console.error('Error sending to Spotify:', error);
                    toast.error('Error al enviar a Spotify');
                  }
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <Music className="w-4 h-4 mr-2" />
                Enviar {selectedForQueue.length} a Spotify
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
